import * as React from "react";
import { Box, useTheme, chakra } from "@chakra-ui/react";
import { cloneDeep } from "lodash-es";
import { Descendant } from "slate";
import { Slate, Editable, RenderPlaceholderProps } from "slate-react";
import { AddOnClickArea, Toolbar } from "./components";
import { RedactorProps, DEFAULT_VALUE, EMPTY_NODE, useInitSlate } from "./duck";

const Redactor: React.FC<RedactorProps> = ({
  onChange,
  value = DEFAULT_VALUE,
  elementTypeProps,
  readOnly = false,
  onBlur,
  placeholder,
  wrapperProps,
  editor: mockEditor,
  toolbarAvailableActions,
  isHidingToolbar = false
}) => {
  const theme = useTheme();
  const { editor, renderElement, renderLeaf } = useInitSlate({
    elementTypeProps,
    editor: mockEditor
  });
  const rootEl = React.useRef<HTMLDivElement>(null);
  const [isVisibleToolbar, setIsVisibleToolbar] = React.useState(
    !isHidingToolbar
  );

  let parsedValue: Descendant[];

  try {
    parsedValue = JSON.parse(value);
  } catch {
    parsedValue = JSON.parse(DEFAULT_VALUE);
  }

  const renderPlaceholder = (props: RenderPlaceholderProps) => {
    /*
    When we add only bulleted or numbered list to redactor, it has only one empty list item,
    and redactor treats it like a single empty block and show placeholder.
    Here we check for bulleted (numbered) list and render empty span instead of placeholder.
    */
    if (
      "type" in parsedValue[0] &&
      (parsedValue[0].type === "bulleted-list" ||
        parsedValue[0].type === "numbered-list")
    ) {
      return <chakra.span />;
    }

    return (
      <Box
        {...props.attributes}
        fontSize="sm"
        fontWeight="normal"
        fontStyle="normal"
        fontFamily="Segoe UI"
        textAlign="left"
        color="gray.700"
      >
        {props.children}
      </Box>
    );
  };

  React.useEffect(() => {
    if (rootEl.current && isHidingToolbar) {
      const onClick = (e: MouseEvent) => {
        if (rootEl.current && !rootEl.current.contains(e.target as Node)) {
          setIsVisibleToolbar(false);
        } else {
          setIsVisibleToolbar(true);
        }
      };
      document.addEventListener("click", onClick);

      return () => document.removeEventListener("click", onClick);
    }
  }, [isHidingToolbar]);

  return (
    <Box
      ref={rootEl}
      {...(!readOnly && {
        borderWidth: 1,
        borderStyle: "solid",
        borderColor: "gray.200",
        backgroundColor: "gray.50",
        borderRadius: "md",
        position: "relative"
      })}
      {...wrapperProps}
    >
      <Slate
        editor={editor}
        value={parsedValue}
        onChange={newValue => {
          if (!newValue.length) {
            /**
             * in some cases it's possible to make slate tree empty
             * then redactor breaks down
             * so just check and insert empty node
             **/
            return editor.insertNode(cloneDeep(EMPTY_NODE));
          }
          if (onChange) onChange(JSON.stringify(newValue));
        }}
      >
        {!readOnly && isVisibleToolbar && (
          <Toolbar
            availableActions={toolbarAvailableActions}
            isHiding={isHidingToolbar}
          />
        )}

        <Box
          {...(!readOnly && {
            position: "relative",
            px: 3,
            py: 1,
            backgroundColor: "white",
            borderRadius: "md",
            fontSize: "md",
            maxH: "2xl",
            overflow: "auto"
          })}
          {...(!isHidingToolbar &&
            !readOnly && {
              borderTop: 1,
              borderTopStyle: "solid",
              borderTopColor: "gray.200"
            })}
        >
          <AddOnClickArea readOnly={readOnly}>
            <Editable
              autoFocus
              readOnly={readOnly}
              renderLeaf={renderLeaf}
              renderElement={renderElement}
              placeholder={placeholder}
              onBlur={onBlur}
              onKeyDown={editor.onKeyDown}
              style={{ tabSize: theme.space["6"] }}
              renderPlaceholder={renderPlaceholder}
            />
          </AddOnClickArea>
        </Box>
      </Slate>
    </Box>
  );
};

export default Redactor;
